import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Modal, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getImportLoading,
  getImportValidationData,
  getRemoveLoading,
  getValidationErrors,
  getSearch,
} from './templates-selector';
import { bulkCloneTemplates, removeTemplate, removeTemplateDrafts } from './templates-actions';
import { getExportLoading } from '../header/header-reducer';
import { getWriteTabsPermissions } from '../../store/user/user-reducer';
import { useLocation } from 'react-router-dom';
import { ExportImportButton, ExportImportButtonText } from '@med-fe/ui';
import { UploadModal } from './UploadModal';
import { ValidationModal } from './ValidationModal';
import { set } from 'lodash';

const { Text } = Typography;

type TemplateRow = {
  key: string;
  template: {
    assigned: boolean;
    templateName: string;
    id: number;
  };
  [key: string]: any;
};

export const TemplatesActions = ({
  selectedKeys,
  selectedRows = [],
  clearSelectedRows,
}: {
  selectedKeys: string[];
  selectedRows?: TemplateRow[];
  clearSelectedRows: () => void;
}) => {
  const deleteConfirmText = 'Please select template(s) to delete.';
  const [visibleConfirmation, setVisibleConfirmation] = useState(false);
  const [isAllSelectedAssigned, setIsAllSelectedAssigned] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(deleteConfirmText);
  const dispatch = useDispatch();
  const removeLoading = useSelector(getRemoveLoading);
  const importValidationData = useSelector(getImportValidationData);
  const validationErrors = useSelector(getValidationErrors);
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [assignTemplateUpload, setAssignTemplateUpload] = useState<boolean>(false);
  const [templateMaintenanceUpload, setTemplateMaintenanceUpload] = useState<boolean>(false);
  const [type, setType] = useState('');
  const [tableId, setTableId] = useState('');
  const [isBulkClone, setIsBulkClone] = useState(false);

  const removeItems = () => {
    let message: any = deleteConfirmText;
    const assignedTemplates = selectedRows.filter(({ template }) => template.assigned);

    if (assignedTemplates.length) {
      const templateNames = assignedTemplates.map(({ template }) => template.templateName);
      message = (
        <>
          <p>
            <span>
              {assignedTemplates.length === selectedRows.length
                ? 'All selected templates are assigned:'
                : 'The next templates are assigned:'}
            </span>
            {templateNames.map((name) => (
              <span key={name}>
                <br />
                <Text strong>{name}</Text>
              </span>
            ))}
          </p>
          {assignedTemplates.length !== selectedRows.length && (
            <p>Are you sure you want to delete other selected templates?</p>
          )}
        </>
      );
    } else if (selectedRows.length > 0) {
      message = (
        <>
          <p>Are you sure you want to delete selected templates?</p>
        </>
      );
    }

    setConfirmMessage(message);
    setVisibleConfirmation(true);
    setIsAllSelectedAssigned(assignedTemplates.length === selectedRows.length);
  };

  const removeDrafts = () => {
    let message: any = 'Please select draft(s) to delete.';

    if (selectedRows.length > 0) {
      message = (
        <>
          <p>Are you sure you want to delete selected draft(s)?</p>
        </>
      );
    }

    setConfirmMessage(message);
    setVisibleConfirmation(true);
    setIsAllSelectedAssigned(false);
  };

  const handleBulkClone = () => {
    let message: any = 'Please select template(s) to clone.';

    if (selectedRows.length > 0) {
      message = (
        <>
          <p>Are you sure you want to clone selected template(s)?</p>
        </>
      );
    }
    setIsBulkClone(true);
    setConfirmMessage(message);
    setVisibleConfirmation(true);
  };

  const handleConfirmDeletion = () => {
    // Use selectedRows directly for deletion
    if (isDrafts) {
      const draftIds = selectedRows
        .filter((row) => row.template && !row.template.assigned)
        .map((row) => +row.key.split('-')[0]);

      if (draftIds.length > 0) {
        dispatch(
          removeTemplateDrafts({
            ids: draftIds,
            successCallback: () => clearSelectedRows(),
          })
        );
      } else {
        clearSelectedRows();
        setVisibleConfirmation(false);
      }
    } else if (isBulkClone) {
      setVisibleConfirmation(false);
      dispatch(
        bulkCloneTemplates({
          selectedRows: selectedRows,
          successCallback: () => {
            setIsBulkClone(false);
            clearSelectedRows();
          },
        })
      );
    } else {
      const unassignedTemplatesIds = selectedRows
        .filter((row) => row.template && !row.template.assigned)
        .map((row) => +row.key.split('-')[0]);

      if (unassignedTemplatesIds.length > 0) {
        dispatch(
          removeTemplate({
            ids: unassignedTemplatesIds,
            successCallback: () => clearSelectedRows(),
          })
        );
      } else {
        // No valid unassigned templates to delete, just close modal and clear selection
        clearSelectedRows();
        setVisibleConfirmation(false);
      }
    }
  };

  const resetConfirmationModal = () => {
    setIsBulkClone(false);
    setIsAllSelectedAssigned(false);
    setVisibleConfirmation(false);
  };

  useEffect(() => {
    if (!removeLoading) {
      resetConfirmationModal();
    }
  }, [removeLoading]);

  useEffect(() => {
    if (importValidationData.length) {
      setAssignTemplateUpload(false);
      setOpenUpload(false);
      setTemplateMaintenanceUpload(false);
    }
  }, [importValidationData]);

  useEffect(() => {
    if (validationErrors.length) {
      setAssignTemplateUpload(false);
      setOpenUpload(false);
      setTemplateMaintenanceUpload(false);
    }
  }, [validationErrors]);

  const handleCancel = () => {
    setAssignTemplateUpload(false);
    setOpenUpload(false);
    setTemplateMaintenanceUpload(false);
  };
  useEffect(() => {
    if (openUpload) {
      setType('Delete Ranges');
      setTableId('templateRanges');
    } else if (assignTemplateUpload && !validationErrors.length) {
      setType('Assign Templates');
      setTableId('assignTemplates');
    } else if (assignTemplateUpload && validationErrors.length) {
      setType('Assign Templates');
      setTableId('assignTemplateValidationErrors');
    } else if (templateMaintenanceUpload) {
      setType('Template Maintenance');
      setTableId('templateMaintenance');
    }
  }, [openUpload, assignTemplateUpload, templateMaintenanceUpload, validationErrors]);
  const search = useSelector(getSearch);
  const isDrafts = !!(search && search.drafts);
  return (
    <TemplatesButtonsWrap>
      <ActionButton type={'ghost'} style={{ display: 'none' }}>
        NEW TEMPLATE
      </ActionButton>
      {isDrafts && (
        <ActionButton type={'ghost'} onClick={() => removeDrafts()}>
          DELETE DRAFT
        </ActionButton>
      )}
      {!isDrafts && (
        <>
          {selectedRows.length > 0 && (
            <ActionButton type={'ghost'} onClick={() => handleBulkClone()}>
              CLONE TEMPLATE{selectedRows.length > 1 ? 'S' : ''}
            </ActionButton>
          )}
          <ActionButton
            type={'ghost'}
            onClick={() => removeItems()}
            disabled={selectedRows.some(({ template }) => template.assigned)}
          >
            DELETE TEMPLATE
          </ActionButton>
        </>
      )}
      <ActionButton type={'ghost'} style={{ display: 'none' }} disabled>
        COPY TEMPLATE
      </ActionButton>
      {!isDrafts && (
        <>
          <ActionButton type={'ghost'} onClick={() => setOpenUpload(true)}>
            DELETE RANGES
          </ActionButton>
          <ActionButton type={'ghost'} onClick={() => setAssignTemplateUpload(true)}>
            ASSIGN TEMPLATES
          </ActionButton>
          <ActionButton type={'ghost'} onClick={() => setTemplateMaintenanceUpload(true)}>
            TEMPLATE MAINTENANCE
          </ActionButton>
        </>
      )}

      <StyledConfirmModal
        title={
          <>
            <ExclamationCircleOutlined />{' '}
            <span className={'confirm-title'}>{isAllSelectedAssigned ? 'Warning' : 'Confirmation'}</span>
          </>
        }
        visible={visibleConfirmation}
        destroyOnClose
        centered
        footer={
          <>
            <Button
              type={'default'}
              onClick={() => {
                resetConfirmationModal();
              }}
            >
              {isAllSelectedAssigned ? 'Cancel' : 'NO'}
            </Button>
            {!isAllSelectedAssigned && (
              <Button
                type={'primary'}
                loading={removeLoading}
                onClick={() => {
                  handleConfirmDeletion();
                }}
              >
                YES
              </Button>
            )}
          </>
        }
      >
        <span>{confirmMessage}</span>
      </StyledConfirmModal>
      <UploadModal
        visible={openUpload ? openUpload : assignTemplateUpload ? assignTemplateUpload : templateMaintenanceUpload}
        data={importValidationData}
        onCancel={handleCancel}
        type={type}
      />
      <ValidationModal
        visible={!!importValidationData.length}
        data={importValidationData}
        type={type}
        tableId={tableId}
      />
      <ValidationModal visible={!!validationErrors.length} data={validationErrors} type={type} tableId={tableId} />
    </TemplatesButtonsWrap>
  );
};

const TemplatesButtonsWrap = styled.div`
  display: flex;
  flex-grow: 1;
`;

const ActionButton = styled(Button)`
  margin-left: 15px;
`;

const StyledConfirmModal = styled(Modal)`
  .ant-modal-content {
    width: initial;
    border-radius: 2px;

    .ant-modal-close {
      display: none;
    }

    .ant-modal-header {
      padding: 16px 24px;
      background: #fff;
      border-bottom: 1px solid #f0f0f0;
      border-radius: 2px;

      .ant-modal-title {
        font-size: 15px;
        color: #ff991f;

        .confirm-title {
          color: #000;
          margin-left: 5px;
        }
      }
    }
  }
`;
